import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';

/**
 * AuthProvider — single source of truth for the (optional) account + the
 * effective plan entitlement. The whole paywall is dormant while the backend
 * reports `paywall_enabled: false`: in that case every feature is unlocked and
 * no paywall UI is shown, so the app behaves exactly as before.
 */
const AuthContext = createContext(null);

const DEFAULT_ENTITLEMENT = {
  plan: 'free',
  status: 'none',
  is_premium: true,
  features: { ad_free: true, batch: true, labels: true, unlimited_history: true },
  limits: { history_max: 200, presets_max: 100 },
  trial_end: null,
  current_period_end: null,
  cancel_at_period_end: false,
  provider: 'none',
  trial_used: false,
  paywall_enabled: false,
  trial_days: 7,
};

function readToken() {
  try { return localStorage.getItem('bcs-token') || sessionStorage.getItem('bcs-token'); } catch (e) { return null; }
}

// Remember which sign-in method was last used ON THIS DEVICE, so the login
// dialog can show a "Last used" hint. Values: 'password' | 'google'.
export function getLastAuthMethod() {
  try { return localStorage.getItem('bc_last_auth') || ''; } catch (e) { return ''; }
}
function setLastAuthMethod(method) {
  try { localStorage.setItem('bc_last_auth', method); } catch (e) { /* ignore */ }
}

// Marker that this device currently believes it has an active session. Used to
// detect an *unexpected* sign-out (e.g. the 30-day inactivity timeout) so we
// can show a friendly "you were signed out after a break" notice — but NOT on a
// deliberate logout (we clear the marker there).
function markHadSession() {
  try { localStorage.setItem('bc_had_session', '1'); } catch (e) { /* ignore */ }
}
function hadSession() {
  try { return localStorage.getItem('bc_had_session') === '1'; } catch (e) { return false; }
}
function clearHadSession() {
  try { localStorage.removeItem('bc_had_session'); } catch (e) { /* ignore */ }
}

export function AuthProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [user, setUser] = useState(null);
  const [entitlement, setEntitlement] = useState(DEFAULT_ENTITLEMENT);
  const [loading, setLoading] = useState(true);
  // True once we detect an unexpected sign-out (idle/session timeout) so the UI
  // can show a friendly one-time notice.
  const [sessionExpired, setSessionExpired] = useState(false);
  // Login prompt modal: opened when a guest triggers a login-gated action
  // (generate/export/download etc.). `authPromptReason` is an optional hint.
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [authPromptReason, setAuthPromptReason] = useState('');
  const promptLogin = useCallback((reason = '') => {
    setAuthPromptReason(reason || '');
    setAuthPromptOpen(true);
  }, []);
  const closeAuthPrompt = useCallback(() => setAuthPromptOpen(false), []);
  // Global pricing/subscription dialog (linked from header, menu, account, SSG).
  const [pricingOpen, setPricingOpen] = useState(false);
  const openPricing = useCallback(() => setPricingOpen(true), []);
  const closePricing = useCallback(() => setPricingOpen(false), []);
  const cancelSubscription = useCallback(async () => {
    const res = await api.cancelSubscription();
    if (res.data && res.data.entitlement) setEntitlement(res.data.entitlement);
    return res.data;
  }, []);

  const reactivateSubscription = useCallback(async () => {
    const res = await api.reactivateSubscription();
    if (res.data && res.data.entitlement) setEntitlement(res.data.entitlement);
    return res.data;
  }, []);

  const persistToken = (tok, remember = true) => {
    try {
      // Always clear both stores first to avoid a stale token lingering.
      localStorage.removeItem('bcs-token');
      sessionStorage.removeItem('bcs-token');
      if (tok) {
        if (remember) localStorage.setItem('bcs-token', tok);
        else sessionStorage.setItem('bcs-token', tok);
      }
    } catch (e) { /* storage unavailable */ }
  };

  const refresh = useCallback(async () => {
    try {
      const res = await api.me();
      const u = res.data.user || null;
      setUser(u);
      setEntitlement(res.data.entitlement || DEFAULT_ENTITLEMENT);
      if (u) {
        markHadSession();
      } else if (hadSession()) {
        // We previously had a session on this device but the server now reports
        // none → the session expired (e.g. 30-day inactivity timeout). Show a
        // friendly notice once and clear any stale token.
        clearHadSession();
        try { localStorage.removeItem('bcs-token'); sessionStorage.removeItem('bcs-token'); } catch (e) { /* ignore */ }
        setSessionExpired(true);
      }
    } catch (e) { /* network error: keep defaults, do NOT treat as sign-out */ }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cfg = await api.getConfig();
        if (mounted) setConfig(cfg.data);
      } catch (e) { /* ignore */ }
      // CRITICAL: if returning from the OAuth callback, skip the /me check here.
      // AuthCallback exchanges the session_id and establishes the session first.
      if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
        if (mounted) setLoading(false);
        return;
      }
      await refresh();
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [refresh]);

  // Emergent-managed Google OAuth: exchange the one-time session_id (from the
  // redirect URL fragment) for an app session cookie, then load the user.
  const loginWithSession = async (sessionId, remember = true) => {
    const res = await api.createSession(sessionId, remember);
    setUser(res.data.user);
    setEntitlement(res.data.entitlement || DEFAULT_ENTITLEMENT);
    setLastAuthMethod('google');
    markHadSession();
    setSessionExpired(false);
    return res.data;
  };

  const login = async (identifier, password, opts = {}) => {
    const { turnstileToken = '', remember = true } = opts || {};
    const body = { identifier, password, remember };
    if (turnstileToken) body.turnstile_token = turnstileToken;
    const res = await api.login(body);
    persistToken(res.data.token, remember);
    setUser(res.data.user);
    setEntitlement(res.data.entitlement || DEFAULT_ENTITLEMENT);
    setLastAuthMethod('password');
    markHadSession();
    setSessionExpired(false);
    return res.data;
  };

  const register = async (email, password, name, username, opts = {}) => {
    const { turnstileToken = '', remember = true } = opts || {};
    const body = { email, password, name, username, remember };
    if (turnstileToken) body.turnstile_token = turnstileToken;
    const res = await api.register(body);
    persistToken(res.data.token, remember);
    setUser(res.data.user);
    setEntitlement(res.data.entitlement || DEFAULT_ENTITLEMENT);
    setLastAuthMethod('password');
    markHadSession();
    setSessionExpired(false);
    return res.data;
  };

  const forgotPassword = async (identifier) => {
    const res = await api.forgotPassword(identifier);
    return res.data;
  };

  const resetPassword = async (identifier, code, newPassword) => {
    const res = await api.resetPassword({ identifier, code, new_password: newPassword });
    return res.data;
  };

  const setUsername = async (username) => {
    const res = await api.setUsername(username);
    if (res.data && res.data.user) setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await api.logout(); } catch (e) { /* ignore */ }
    persistToken(null);
    clearHadSession();      // deliberate logout: do not show the "expired" notice
    setSessionExpired(false);
    setUser(null);
    await refresh();
  };

  const redeemLicense = async (key) => {
    const res = await api.redeemLicense(key);
    try { localStorage.setItem('bcs-license', (key || '').trim().toUpperCase()); } catch (e) { /* ignore */ }
    setEntitlement(res.data.entitlement || DEFAULT_ENTITLEMENT);
    return res.data;
  };

  const startTrial = async (plan = 'monthly') => {
    const res = await api.startTrial(plan);
    setEntitlement(res.data.entitlement || DEFAULT_ENTITLEMENT);
    return res.data;
  };

  const paywallEnabled = !!(config ? config.paywall_enabled : entitlement.paywall_enabled);
  const features = entitlement.features || DEFAULT_ENTITLEMENT.features;
  const isPremium = paywallEnabled ? !!entitlement.is_premium : true;

  const value = {
    config,
    plans: (config && config.plans) || [],
    trialDays: (config && config.trial_days) || entitlement.trial_days || 7,
    user,
    isAuthenticated: !!user,
    entitlement,
    loading,
    paywallEnabled,
    isPremium,
    isPaid: !!entitlement.is_paid,
    features,
    plan: entitlement.plan || 'free',
    status: entitlement.status || 'none',
    hasFeature: (f) => (paywallEnabled ? !!features[f] : true),
    hasToken: !!readToken(),
    sessionExpired,
    ackSessionExpired: () => setSessionExpired(false),
    authPromptOpen,
    authPromptReason,
    promptLogin,
    closeAuthPrompt,
    pricingOpen,
    openPricing,
    closePricing,
    cancelSubscription,
    reactivateSubscription,
    login,
    register,
    forgotPassword,
    resetPassword,
    setUsername,
    loginWithSession,
    logout,
    redeemLicense,
    startTrial,
    refresh,
    updateUser: (u) => setUser(u),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Safe fallback so components never crash if rendered outside the provider.
    return {
      config: null, plans: [], trialDays: 7, user: null, isAuthenticated: false,
      entitlement: DEFAULT_ENTITLEMENT, loading: false, paywallEnabled: false,
      isPremium: true, features: DEFAULT_ENTITLEMENT.features, plan: 'free', status: 'none', isPaid: false,
      hasFeature: () => true, hasToken: false,
      sessionExpired: false, ackSessionExpired: () => {},
      authPromptOpen: false, authPromptReason: '', promptLogin: () => {}, closeAuthPrompt: () => {},
      pricingOpen: false, openPricing: () => {}, closePricing: () => {},
      cancelSubscription: async () => {},
      reactivateSubscription: async () => {},
      login: async () => {}, register: async () => {}, loginWithSession: async () => {}, logout: async () => {},
      forgotPassword: async () => {}, resetPassword: async () => {}, setUsername: async () => {},
      redeemLicense: async () => {}, startTrial: async () => {}, refresh: async () => {},
      updateUser: () => {},
    };
  }
  return ctx;
}
